import { useState, useCallback, useMemo } from 'react'
import { Button, Tooltip } from '@mui/material'
import { QrCode2, Share } from '@mui/icons-material'
import type { CustomAction, FieldMeta, ApiResponse } from '@crm/shared'
import { interpolateTemplate } from '../../utils/interpolate'
import { createActionLog } from '../../api/custom-actions'
import { QrCodeDialog } from './QrCodeDialog'
import { ShareDialog } from './ShareDialog'
import { useApi } from '../../api/client'
import { getIconComponent } from '../../routes/settings/ObjectManagement'

const ACTION_TYPE_DEFAULT_ICONS: Record<string, React.ReactElement> = {
  qr_code: <QrCode2 fontSize="small" />,
  share: <Share fontSize="small" />,
}

interface CustomActionButtonsProps {
  objectId: string
  recordId: string
  recordData: Record<string, unknown>
  systemFields: Record<string, unknown>
  fields: FieldMeta[]
  fieldPermissions: Record<string, string>
}

export function CustomActionButtons({
  objectId,
  recordId,
  recordData,
  systemFields,
  fields,
  fieldPermissions,
}: CustomActionButtonsProps) {
  const { data: actionsData } = useApi<ApiResponse<CustomAction[]>>(
    objectId ? `/objects/${objectId}/custom-actions` : null
  )
  const actions = actionsData?.data ?? []

  const [qrDialog, setQrDialog] = useState<{ open: boolean; title: string; content: string }>({
    open: false, title: '', content: '',
  })
  const [shareDialog, setShareDialog] = useState<{ open: boolean; title: string; content: string }>({
    open: false, title: '', content: '',
  })

  const hiddenFieldNames = useMemo(() => {
    const hidden = new Set<string>()
    for (const field of fields) {
      if (fieldPermissions[field.field_id] === 'hidden') {
        hidden.add(field.name)
      }
    }
    return hidden
  }, [fields, fieldPermissions])

  const handleClick = useCallback(async (action: CustomAction) => {
    const content = action.template
      ? interpolateTemplate(action.template, recordData, systemFields, hiddenFieldNames)
      : ''

    if (action.action_type === 'qr_code') {
      setQrDialog({ open: true, title: action.label, content })
    } else if (action.action_type === 'share') {
      setShareDialog({ open: true, title: action.label, content })
    }

    createActionLog(objectId, recordId, {
      custom_action_id: action.id,
      action_type: action.action_type,
      action_name: action.name,
      action_label: action.label,
    }).catch(() => { /* fire-and-forget */ })
  }, [objectId, recordId, recordData, systemFields, hiddenFieldNames])

  if (actions.length === 0) return null

  return (
    <>
      {actions.map((action) => {
        const IconComp = action.icon ? getIconComponent(action.icon) : null
        const icon = IconComp
          ? <IconComp fontSize="small" />
          : (ACTION_TYPE_DEFAULT_ICONS[action.action_type] ?? <Share fontSize="small" />)
        return (
          <Tooltip key={action.id} title={action.label}>
            <Button
              size="small"
              variant="outlined"
              startIcon={icon}
              onClick={() => handleClick(action)}
            >
              {action.label}
            </Button>
          </Tooltip>
        )
      })}
      <QrCodeDialog
        open={qrDialog.open}
        onClose={() => setQrDialog((p) => ({ ...p, open: false }))}
        title={qrDialog.title}
        content={qrDialog.content}
      />
      <ShareDialog
        open={shareDialog.open}
        onClose={() => setShareDialog((p) => ({ ...p, open: false }))}
        title={shareDialog.title}
        content={shareDialog.content}
      />
    </>
  )
}
